import type { introspection_types } from "@/types/graphql-env.d.ts";
import { differenceInDays, format, parse, subDays } from "date-fns";

const DATE_FORMAT = "yyyy-MM-dd";

/**
 * Converts URL search param dates (strings) to Date objects.
 * Parses as LOCAL time to avoid timezone shifting issues with date pickers.
 */
export const parseDateString = (dateStr?: string): Date | undefined => {
  if (!dateStr) return undefined;

  try {
    const result = parse(dateStr, DATE_FORMAT, new Date());
    return Number.isNaN(result.getTime()) ? undefined : result;
  } catch {
    return undefined;
  }
};

/**
 * Converts Date object to YYYY-MM-DD string for URL.
 * Uses local date parts to avoid timezone shifting.
 */
export const formatDateForUrl = (date: Date): string => {
  return format(date, DATE_FORMAT);
};

/**
 * Calculates start and end dates based on filter days
 * @param filterDays - Number of days to go back from today
 * @returns Object with startDate and endDate
 */
export const calculateDateRangeFromDays = (filterDays: number) => {
  const endDate = new Date();
  const startDate = subDays(endDate, filterDays);

  return {
    startDate,
    endDate,
  };
};

export const getFormattedDateRangeFromParams = (
  filterDays: number,
  startDate: Date | undefined,
  endDate: Date | undefined,
  isCustomDaysRange: boolean,
  formatString = "yyyy-MM-dd",
) => {
  const { startDate: startOfRange, endDate: endOfRange } =
    isCustomDaysRange && startDate && endDate
      ? { startDate, endDate }
      : calculateDateRangeFromDays(filterDays);

  const formattedStartDate = format(startOfRange, formatString);
  const formattedEndDate = format(endOfRange, formatString);

  return {
    startDate: formattedStartDate,
    endDate: formattedEndDate,
  };
};

/**
 * RelativeDateRange enum type extracted from GraphQL schema
 */
export type RelativeDateRange =
  introspection_types["RelativeDateRange"]["enumValues"];

/**
 * Bidirectional lookup table for RelativeDateRange enum and number of days
 */
const RELATIVE_DATE_RANGE_LOOKUP: Record<RelativeDateRange, number> = {
  TODAY: 1,
  LAST_3_DAYS: 3,
  LAST_7_DAYS: 7,
  LAST_30_DAYS: 30,
  LAST_60_DAYS: 60,
  LAST_90_DAYS: 90,
};

/**
 * Reverse lookup: days to RelativeDateRange enum
 */
const DAYS_TO_RELATIVE_DATE_RANGE: Record<number, RelativeDateRange> = {
  1: "TODAY",
  3: "LAST_3_DAYS",
  7: "LAST_7_DAYS",
  30: "LAST_30_DAYS",
  60: "LAST_60_DAYS",
  90: "LAST_90_DAYS",
};

/**
 * Converts RelativeDateRange enum to number of days
 * @param period - RelativeDateRange enum value
 * @returns Number of days, or undefined if period is not found
 */
export const relativeDateRangeToDays = (
  period: RelativeDateRange,
): number | undefined => {
  return RELATIVE_DATE_RANGE_LOOKUP[period];
};

/**
 * Converts number of days to RelativeDateRange enum
 * @param days - Number of days
 * @returns RelativeDateRange enum value, or undefined if days doesn't match any enum
 */
export const daysToRelativeDateRange = (
  days: number,
): RelativeDateRange | undefined => {
  return DAYS_TO_RELATIVE_DATE_RANGE[days];
};

/** Graph aggregation period for volume charts (day / week / month). */
export type DateRangePeriod = "day" | "week" | "month";

/**
 * Returns the graph period to use for a given date range.
 * Used to switch volume chart view: ≤31 days → day, 32–89 → week, ≥90 → month.
 *
 * @param startDate - Range start (used when endDate also provided)
 * @param endDate - Range end (used when startDate also provided)
 * @param days - Preset number of days; used when start/end not both provided
 * @returns "day" | "week" | "month", or null if range cannot be determined
 */
export const getPeriodForDateRange = (
  startDate: Date | undefined,
  endDate: Date | undefined,
  days: number | undefined,
): DateRangePeriod | null => {
  let daysDifference = days;

  if (startDate && endDate) {
    daysDifference = differenceInDays(endDate, startDate);
  }

  if (daysDifference !== undefined) {
    if (daysDifference <= 31) return "day";
    if (daysDifference < 90) return "week";
    return "month";
  }

  return null;
};
